import React from 'react';
import {produce} from 'immer';
import {CreateUndoRedoReducer, UndoRedoState} from 'common/reducer/undo-redo';
import {createVisit, RouteVisit, visitsFromIDs} from 'common/flight-leg/route-visits';

export const DefaultState = {
    initPath: [] as RouteVisit[],
    departureID: '',
    path: {present: [], past: [], future: []} as UndoRedoState<RouteVisit[]>,
    changed: false
};
export type State = typeof DefaultState;
export type Action = {type: 'INIT'; payload: {flightPath: string[]; departureID?: string}} |
    {type: 'RESET' | 'UNDO' | 'REDO' | 'REPAIR_START'} |
    {type: 'UPDATE_ROUTE'; payload: {removeIndex?: number; add?: {locId: string; insertIndex?: number}}} |
    {type: 'MOVE'; payload: {startIndex: number; endIndex: number}};

export const Reducer: React.Reducer<State, Action> = (previous, action) => produce(previous, state => {
    const history = CreateUndoRedoReducer<RouteVisit[]>();
    if (action.type === 'INIT') {
        const ids = action.payload.flightPath;
        state.departureID = action.payload.departureID || ids[0] || '';
        state.initPath = visitsFromIDs(ids.length ? ids : state.departureID ? [state.departureID] : []);
        state.path = {present: state.initPath, past: [], future: []};
        state.changed = false;
        return;
    }
    if (action.type === 'RESET') {
        state.path = {present: state.initPath, past: [], future: []};
        state.changed = false;
        return;
    }
    if (action.type === 'UNDO' || action.type === 'REDO') {
        state.path = history(previous.path, {type: action.type});
    } else {
        const visits = [...state.path.present];
        if (action.type === 'REPAIR_START') {
            if (!state.departureID || visits[0]?.locationID === state.departureID) return;
            visits.unshift(createVisit(state.departureID));
        } else if (action.type === 'MOVE') {
            const {startIndex, endIndex} = action.payload;
            if (startIndex < 0 || endIndex < 0 || startIndex >= visits.length ||
                endIndex >= visits.length || startIndex === endIndex) return;
            visits.splice(endIndex, 0, visits.splice(startIndex, 1)[0]);
        } else if (action.type === 'UPDATE_ROUTE') {
            const {removeIndex, add} = action.payload;
            if (removeIndex !== undefined) {
                if (!Number.isInteger(removeIndex) || removeIndex < 0 || removeIndex >= visits.length) return;
                visits.splice(removeIndex, 1);
            }
            if (add) {
                const index = add.insertIndex ?? visits.length;
                if (!Number.isInteger(index) || index < 0 || index > visits.length || !add.locId) return;
                visits.splice(index, 0, createVisit(add.locId));
            }
        }
        state.path = history(previous.path, {type: 'UPDATE', payload: visits});
    }
    state.changed = JSON.stringify(state.path.present) !== JSON.stringify(state.initPath);
});
